import { TOKENS, fmtMoney } from '../theme';

export function isModified(row) {
  return Boolean(row?.modified);
}

export function isDeleted(row) {
  return row?.status === 'deleted' || row?.entryStatus === 'deleted';
}

export function badgeProps(row) {
  if (isDeleted(row)) {
    return { label: 'ELIMINADO', bg: TOKENS.red100, color: TOKENS.red700 };
  }
  if (isModified(row)) {
    return { label: 'MODIFICADO', bg: TOKENS.amber100, color: TOKENS.amber700 };
  }
  return null;
}

const ENTITY_META = {
  BONUS: { label: 'Bonus', bg: TOKENS.amber50, color: TOKENS.amber700 },
  RAFFLE: { label: 'Raffle', bg: TOKENS.pink50, color: TOKENS.pink700 },
  TICKET_OUT: {
    label: 'Ticket Out',
    bg: TOKENS.violet50,
    color: TOKENS.violet700,
  },
  TRANSACTION_BANK: {
    label: 'Bank',
    bg: TOKENS.emerald50,
    color: TOKENS.emerald700,
  },
  TRANSACTION_EXPENSE: {
    label: 'Expense',
    bg: TOKENS.red50,
    color: TOKENS.red700,
  },
  POINT: { label: 'Point Match', bg: TOKENS.blue50, color: TOKENS.blue700 },
};

export function entityMeta(entityType) {
  return (
    ENTITY_META[entityType] || {
      label: (entityType || 'Entry').replace(/_/g, ' ').toLowerCase(),
      bg: TOKENS.slate100,
      color: TOKENS.slate700,
    }
  );
}

export function actionMeta(entry) {
  const action = entry?.action;
  if (action === 'CREATE') {
    return { label: 'ADDED', bg: TOKENS.blue100, color: TOKENS.blue700 };
  }
  if (action === 'EDIT') {
    return { label: 'EDITED', bg: TOKENS.amber100, color: TOKENS.amber700 };
  }
  if (action === 'DELETE') {
    return { label: 'DELETED', bg: TOKENS.red100, color: TOKENS.red700 };
  }
  if (action === 'DECISION') {
    const d = entry?.newValues?.decision;
    if (d === 'APPROVED') {
      return {
        label: 'APPROVED',
        bg: TOKENS.emerald100,
        color: TOKENS.emerald700,
      };
    }
    if (d === 'FLAGGED') {
      return { label: 'FLAGGED', bg: TOKENS.red100, color: TOKENS.red700 };
    }
    return { label: 'REVIEWED', bg: TOKENS.slate100, color: TOKENS.slate700 };
  }
  return {
    label: action || 'CHANGED',
    bg: TOKENS.slate100,
    color: TOKENS.slate700,
  };
}

const FIELD_LABELS = {
  amount: 'Amount',
  bonusAmount: 'Amount',
  ticketOut: 'Ticket Amount',
  machine: 'Machine',
  customerId: 'Customer',
  selectedCustomerId: 'Customer',
  selectedEmployeeId: 'Employee',
  manageBonusId: 'Bonus Type',
  specify: 'Specification',
  description: 'Description',
  comments: 'Comments',
  nameInput: 'Name',
  name: 'Name',
  time: 'Time',
  date: 'Date',
  winnerNumber: 'Winner #',
  type: 'Type',
  expenseType: 'Expense Type',
  amounts: 'Denominations',
  decision: 'Decision',
};

export function fieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

const MONEY_FIELDS = new Set(['amount', 'bonusAmount', 'ticketOut']);
const CUSTOMER_ID_FIELDS = new Set(['customerId', 'selectedCustomerId']);
const EMPLOYEE_ID_FIELDS = new Set(['selectedEmployeeId']);
const BONUS_TYPE_FIELDS = new Set(['manageBonusId']);

function resolveCustomerName(id, customers) {
  if (!id || !Array.isArray(customers)) return null;
  const c = customers.find((x) => x.customerId === id);
  if (!c) return null;
  return `${c.firstName || ''} ${c.lastName || ''}`.trim() || null;
}

function resolveEmployeeName(id, employees) {
  if (!id || !Array.isArray(employees)) return null;
  const e = employees.find((x) => x.userId === id);
  if (!e) return null;
  return (
    [e.firstname, e.lastname].filter(Boolean).join(' ') || e.displayName || null
  );
}

function resolveBonusTypeName(id, manageBonusList) {
  if (!id || !Array.isArray(manageBonusList)) return null;
  const b = manageBonusList.find((x) => x.manageBonusId === id);
  return b?.name || null;
}

// Returns a renderable string for the given field. lookups: {customers,
// employees, manageBonusList} — any subset; missing lookups fall through to
// the raw id (so we never crash on missing context).
export function formatFieldValue(field, value, lookups = {}) {
  if (value == null || value === '') return '—';
  if (MONEY_FIELDS.has(field)) return fmtMoney(value);
  if (CUSTOMER_ID_FIELDS.has(field)) {
    return resolveCustomerName(value, lookups.customers) || String(value);
  }
  if (EMPLOYEE_ID_FIELDS.has(field)) {
    return resolveEmployeeName(value, lookups.employees) || String(value);
  }
  if (BONUS_TYPE_FIELDS.has(field)) {
    return (
      resolveBonusTypeName(value, lookups.manageBonusList) || String(value)
    );
  }
  if (field === 'amounts' && typeof value === 'object') {
    const parts = Object.entries(value)
      .filter(([, v]) => Number(v) > 0)
      .map(([k, v]) => `${k}×${v}`);
    return parts.length ? parts.join(' ') : '—';
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Compact one-line entity identifier shown next to the entity badge. For
// BONUS rows we show bonus type · spec · machine · customer · amount so
// the manager can locate the entry without expanding it.
export function entityIdentifier(entry, lookups = {}) {
  const v = { ...(entry?.oldValues || {}), ...(entry?.newValues || {}) };
  const parts = [];

  if (entry?.entityType === 'BONUS') {
    const bonusType = resolveBonusTypeName(
      v.manageBonusId,
      lookups.manageBonusList
    );
    if (bonusType) parts.push(bonusType);
    if (v.specify) parts.push(v.specify);
    if (v.machine) parts.push(`Machine ${v.machine}`);
    const cust = resolveCustomerName(v.customerId, lookups.customers);
    if (cust) parts.push(cust);
    if (v.bonusAmount != null) parts.push(fmtMoney(v.bonusAmount));
    return parts.join(' · ');
  }

  // Identity-only — drop money from the line since the change diff already
  // shows old → new amounts; repeating it as "FOOD · $90" is noise.
  if (v.machine) parts.push(`Machine ${v.machine}`);
  if (v.description) parts.push(v.description);
  if (v.specify) parts.push(v.specify);
  if (v.winnerNumber) parts.push(`Winner #${v.winnerNumber}`);
  // For Raffle, `name` is the cached customer label — picking a customer
  // sets both, so showing them side-by-side renders "X · X". De-dupe.
  const cust = resolveCustomerName(v.customerId, lookups.customers);
  if (v.name && v.name !== cust) parts.push(v.name);
  if (cust) parts.push(cust);
  return parts.join(' · ');
}

// Per-field old → new pairs. Suppresses rows where both sides render to the
// same value after id-resolution (e.g. "Comments: — -> —" or no actual diff
// once we map ids to names).
export function changeRows(entry, lookups = {}) {
  if (!entry) return [];
  const action = entry.action;
  if (action === 'DECISION') {
    return [
      {
        field: 'decision',
        label: 'Decision',
        oldVal: '—',
        newVal: formatFieldValue(
          'decision',
          entry.newValues?.decision,
          lookups
        ),
      },
    ];
  }
  const changedFields = entry.changedFields || [];
  let fields = changedFields;
  if (!fields.length) {
    const src = action === 'DELETE' ? entry.oldValues : entry.newValues;
    fields = src ? Object.keys(src) : [];
  }
  // Raffle stores the customer's display name in `name`; selecting a new
  // customer mutates both fields, producing a redundant "Name X -> Y" row
  // next to "Customer X -> Y". Drop the name row when it mirrors the
  // customer change.
  const dropRaffleNameMirror =
    entry.entityType === 'RAFFLE' &&
    fields.includes('customerId') &&
    fields.includes('name') &&
    entry.newValues?.name ===
      resolveCustomerName(entry.newValues?.customerId, lookups.customers) &&
    entry.oldValues?.name ===
      resolveCustomerName(entry.oldValues?.customerId, lookups.customers);
  return fields
    .map((f) => ({
      field: f,
      label: fieldLabel(f),
      oldVal: formatFieldValue(f, entry.oldValues?.[f], lookups),
      newVal: formatFieldValue(f, entry.newValues?.[f], lookups),
    }))
    .filter(
      (r) =>
        r.oldVal !== r.newVal && !(dropRaffleNameMirror && r.field === 'name')
    );
}

export function formatChangedAt(changedAt) {
  if (!changedAt) return '';
  const d = changedAt instanceof Date ? changedAt : new Date(changedAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Mirrors ReportService.addTransactionsTable: appends the linked customer
// (preferred) or employee name in parentheses so descriptions like ZELLE,
// CUSTOMER LOANS, EXTRA MATCH / LUCKY, EMPLOYEE SHORT PAYMENT, TRANSFER
// MONEY TO X carry the counterparty inline.
export function enrichTxnDescription(row, customers = [], employees = []) {
  const base = row?.description || '';
  if (!base) return base;
  const cust = resolveCustomerName(row?.selectedCustomerId, customers);
  if (cust) return `${base} (${cust})`;
  const emp = resolveEmployeeName(row?.selectedEmployeeId, employees);
  if (emp) return `${base} (${emp})`;
  return base;
}
